  import { Injectable } from '@angular/core';
  import { HttpClient } from '@angular/common/http';
  import { Observable, of } from 'rxjs';
  import { map } from 'rxjs/operators';
  import { Video, VideoCategory } from '../models/video.model';
  import { environment } from '../../../environments/environment';
  
  @Injectable({
    providedIn: 'root'
  })
  export class VideosService {
    private apiUrl = `${environment.apiUrl}/videos`;
    private configApiUrl = `${environment.apiUrl}/configurations`;
    private savedVideosKey = 'SAVED_VIDEOS';
  
    constructor(private http: HttpClient) { }

  // Métodos para videos
  getVideos(): Observable<Video[]> {
    return this.http.get<any>(this.apiUrl).pipe(
      map(response => response.data || [])
    );
  }

  getVideoById(id: string): Observable<Video> {
    return this.http.get<any>(`${this.apiUrl}/${id}`).pipe(
      map(response => response.data)
    );
  }

  createVideo(video: Video): Observable<Video> {
    return this.http.post<any>(this.apiUrl, video).pipe(
      map(response => response.data)
    );
  }

  updateVideo(id: string, video: Video): Observable<Video> {
    return this.http.patch<any>(`${this.apiUrl}/${id}`, video).pipe(
      map(response => response.data)
    );
  }

  deleteVideo(id: string): Observable<void> {
    return this.http.delete<any>(`${this.apiUrl}/${id}`).pipe(
      map(response => response.data)
    );
  }

  incrementViews(id: string): Observable<Video> {
    return this.http.patch<any>(`${this.apiUrl}/${id}/views`, {}).pipe(
      map(response => response.data)
    );
  }

  toggleFeatured(id: string): Observable<Video> {
    return this.http.patch<any>(`${this.apiUrl}/${id}/featured`, {}).pipe(
      map(response => response.data)
    );
  }

    // Métodos para videos guardados
  saveVideo(video: Video): Observable<boolean> {
    try {
      const savedVideos = this.getSavedVideosFromStorage();
      // Verificar si el video ya está guardado
      if (!savedVideos.some(v => v._id === video._id)) {
        savedVideos.push(video);
        localStorage.setItem(this.savedVideosKey, JSON.stringify(savedVideos));
      }
      return of(true);
    } catch (error) {
      console.error('Error al guardar video:', error);
      return of(false);
    }
  }

  unsaveVideo(videoId: string): Observable<boolean> {
    try {
      let savedVideos = this.getSavedVideosFromStorage();
      savedVideos = savedVideos.filter(v => v._id !== videoId);
      localStorage.setItem(this.savedVideosKey, JSON.stringify(savedVideos));
      return of(true);
    } catch (error) {
      console.error('Error al eliminar video guardado:', error);
      return of(false);
    }
  }

  getSavedVideos(): Observable<Video[]> {
    return of(this.getSavedVideosFromStorage());
  }

  isVideoSaved(videoId: string): boolean {
    const savedVideos = this.getSavedVideosFromStorage();
    return savedVideos.some(v => v._id === videoId);
  }

  private getSavedVideosFromStorage(): Video[] {
    try {
      const savedVideosJson = localStorage.getItem(this.savedVideosKey);
      return savedVideosJson ? JSON.parse(savedVideosJson) : [];
    } catch (error) {
      console.error('Error al obtener videos guardados:', error);
      return [];
    }
  }

  // Métodos para categorías de videos
  getVideoCategories(): Observable<VideoCategory[]> {
    return this.http.get<any>(`${this.configApiUrl}/video-categories`).pipe(
      map(response => response.data || [])
    );
  }

  getVideoCategoryById(id: string): Observable<VideoCategory> {
    return this.http.get<any>(`${this.configApiUrl}/video-categories/${id}`).pipe(
      map(response => response.data)
    );
  }

  createVideoCategory(category: VideoCategory): Observable<VideoCategory> {
    return this.http.post<any>(`${this.configApiUrl}/video-categories`, category).pipe(
      map(response => response.data)
    );
  }

  updateVideoCategory(id: string,category: VideoCategory): Observable<VideoCategory> {
    return this.http.put<any>(`${this.configApiUrl}/video-categories/${id}`, category).pipe(
      map(response => response.data)
    );
  }

  deleteVideoCategory(id: string): Observable<void> {
    return this.http.delete<any>(`${this.configApiUrl}/video-categories/${id}`).pipe(
      map(response => response.data)
    );
  }

  // Extraer ID de YouTube del URL
  getYoutubeVideoId(url: string): string {
    if (!url) return '';
    
    // Manejar diferentes formatos de URL de YouTube
    const patterns = [
      /(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^#&?]*)/,
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1] && match[1].length === 11) {
        return match[1];
      }
    }
    
    // Último intento con expresión regular simple
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    
    return (match && match[2] && match[2].length === 11)
      ? match[2]
      : '';
  }

  // Generar URL de miniatura de YouTube
  getYoutubeThumbnailUrl(videoUrl: string): string {
    const videoId = this.getYoutubeVideoId(videoUrl);
    if (!videoId) return '';
    
    return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
  }

  // Generar URL de embed de YouTube
  getYoutubeEmbedUrl(videoUrl: string): string {
    const videoId = this.getYoutubeVideoId(videoUrl);
    if (!videoId) return '';
    
    // Añadir parámetros para mejorar la experiencia de usuario
    return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`;
  }
} 